import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Ticket } from "lucide-react";
import { NAV, BUY_URL } from "@/data";

const LIGHT_HERO = ["/buscar-viajes", "/boleterias"];

export default function Header() {
    const { pathname } = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [pathname]);

    const orangeHero = LIGHT_HERO.includes(pathname);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
                scrolled
                    ? "bg-white/95 backdrop-blur-md shadow-[0_6px_30px_rgba(0,0,0,0.06)]"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex h-20 items-center justify-between gap-4">
                    <Link
                        to="/"
                        data-testid="header-logo"
                        className="flex items-center shrink-0"
                    >
                        <img
                            src="/images/logo.webp"
                            alt="El Cometa Bis"
                            className="h-9 w-auto object-contain"
                        />
                    </Link>

                    <nav className="hidden lg:flex items-center gap-7">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                data-testid={`nav-${item.to === "/" ? "inicio" : item.to.slice(1)}`}
                                className={({ isActive }) => {
                                    const base =
                                        "text-[13px] font-semibold uppercase tracking-wide transition-colors";
                                    if (scrolled)
                                        return `${base} ${isActive ? "text-brand" : "text-ink hover:text-brand"}`;
                                    if (orangeHero)
                                        return `${base} ${isActive ? "text-white underline underline-offset-8 decoration-2" : "text-white/85 hover:text-white"}`;
                                    return `${base} ${isActive ? "text-brand" : "text-white/90 hover:text-white"}`;
                                }}
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <a
                            href={BUY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="header-buy-button"
                            className={`hidden sm:inline-flex items-center gap-2 rounded-full px-6 py-3 text-[13px] font-bold uppercase tracking-wide shadow-lg transition-transform hover:-translate-y-0.5 ${
                                !scrolled && orangeHero
                                    ? "bg-ink text-white shadow-black/20 hover:bg-black"
                                    : "bg-brand text-white shadow-brand/30 hover:bg-brand-dark"
                            }`}
                        >
                            <Ticket className="h-4 w-4" />
                            Comprar Pasajes
                        </a>
                        <button
                            onClick={() => setOpen((v) => !v)}
                            data-testid="mobile-menu-toggle"
                            aria-label="Menú"
                            className={`lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors ${
                                scrolled
                                    ? "bg-ink/5 text-ink"
                                    : "bg-white/15 text-white"
                            }`}
                        >
                            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden border-t border-black/5 bg-white px-4 pb-6 pt-2"
                >
                    <nav className="flex flex-col">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                className={({ isActive }) =>
                                    `py-3 text-sm font-semibold uppercase tracking-wide border-b border-black/5 ${
                                        isActive ? "text-brand" : "text-ink"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                    <a
                        href={BUY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white"
                    >
                        <Ticket className="h-4 w-4" />
                        Comprar Pasajes
                    </a>
                </div>
            )}
        </header>
    );
}
